<template>
  <div class="d-flex flex-column">
    <template v-if="holder">
    <div class="info-list">
      <div class="list-head">
        <h6 class="bold">個人資料</h6>
        <v-btn
          v-if="editable && onEdit" @click="onEdit(holder, holder.profile)"
          variant="plain" class="btn-edit">
          <v-icon icon="edit" size="18" class="mr-1" />編輯
        </v-btn>
      </div>
      <div class="list-body">
        <div v-for="field in profileFields" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              <!-- personal_name => 認購人名稱 -->
              {{ field.name.replace(/^新/, '') }}
            </div>
          </div>
          <!-- 修改前後對照 -->
          <div class="group">
            <!-- 沒有修改過 -->
            <div class="field content" v-if="!isDraftChanged(field.id) && !hideEmptyDraftValue(field.id)">
              <span v-if="showComparisonMeta(field.id)" class="data-label">{{ currentDataLabel }}</span>
              <div class="font-body-2">
                {{ formatField(getDraftField(field.id)) }}
              </div>
              <span v-if="showComparisonMeta(field.id) && formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
            </div>
            <!-- 有修改過 -->
            <template v-else-if="isDraftChanged(field.id)">
              <!-- 正式資料 -->
              <div class="field content font-body-2 old-data">
                <span class="data-label">上次填寫</span>
                <TextDiff onlyRemoved :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                <span v-if="formatDataMonth(formalSubmittedAt)" class="data-month font-body-2">{{ formatDataMonth(formalSubmittedAt) }}</span>
              </div>
              <!-- 草稿/本次填寫 -->
              <div class="field content font-body-2">
                <span class="data-label">{{ currentDataLabel }}</span>
                <TextDiff onlyAdded :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                <span v-if="formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
              </div>
            </template>
            <!-- 保銀留存資料只作為比對依據 -->
            <div v-for="(reference, index) in referenceRows(field.id)" :key="`${field.id}-${index}`" class="field content font-body-2 reference-data">
              <span v-if="showComparisonMeta(field.id)" class="reference-label">
                {{ referenceLabel(field.id, index) }}
                <small v-if="referenceOwnerName(reference)" class="reference-owner">{{ referenceOwnerName(reference) }}</small>
              </span>
              <span class="reference-value">{{ formatField(reference.value) }}</span>
              <span v-if="showComparisonMeta(field.id) && formatDataMonth(reference.rpt_date || reference.upd_datetime)" class="data-month font-body-2">{{ formatDataMonth(reference.rpt_date || reference.upd_datetime) }}</span>
            </div>
          </div>
        </div>
        <!-- 帳戶層級中國籍受益人聲明；Nominee 的中國籍資訊屬於交易/庫存，不顯示在客戶資料。 -->
        <div v-if="accountType !== AccountType.Nominee && !editable" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              {{ chinaDeclarationLabel }}
            </div>
          </div>
          <div class="field content">
            <!-- Admin 需要特別 Highlight -->
            <div class="font-body-2" :class="{ highlight: isAdmin && holder?.kyc?.statement?.is_beneficiary_in_china }">
              {{ holder?.kyc?.statement?.is_beneficiary_in_china ? '是' : '否' }}
            </div>
          </div>
        </div>
        <!-- PEP: 僅客戶資料頁顯示，交易流程內的資料確認頁不顯示 -->
        <div v-if="!editable" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              是否為重要政治性職務人士
            </div>
          </div>
          <div class="field content">
            <!-- Admin 需要特別 Highlight -->
            <div class="font-body-2" :class="{ highlight: isAdmin && holder?.kyc?.questionnaire?.is_politically_exposed_person }">
              {{ holder?.kyc?.questionnaire?.is_politically_exposed_person ? '是' : '否' }}
            </div>
          </div>
        </div>
        <div v-if="!editable" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              聲明為受限人士
            </div>
          </div>
          <div class="field content">
            <div class="font-body-2" :class="{ highlight: isAdmin && holder?.kyc?.statement?.restricted_person_explain }">
              {{ holder?.kyc?.statement?.restricted_person_explain ? '是' : '否' }}
            </div>
          </div>
        </div>
        <div v-if="!editable" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              聲明為覆蓋人士
            </div>
          </div>
          <div class="field content">
            <div class="font-body-2" :class="{ highlight: isAdmin && holder?.kyc?.statement?.covered_person_explain }">
              {{ holder?.kyc?.statement?.covered_person_explain ? '是' : '否' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="info-list mt-8">
      <div class="list-head">
        <h6 class="bold">聯絡資料</h6>
        <v-btn
          v-if="editable && onEdit" @click="onEdit(holder, holder.contact)"
          variant="plain" class="btn-edit">
          <v-icon icon="edit" size="18" class="mr-1" />編輯
        </v-btn>
      </div>
      <div class="list-body">
        <div v-for="field in contactFields" class="body-row">
          <div class="field title">
            <div class="font-body-2 bold">
              {{ field.name }}
            </div>
          </div>
          <!-- 修改前後對照 -->
          <div class="group">
            <!-- 沒有修改過 -->
            <div class="field content" v-if="!isDraftChanged(field.id) && !hideEmptyDraftValue(field.id)">
              <span v-if="showComparisonMeta(field.id)" class="data-label">{{ currentDataLabel }}</span>
              <div class="font-body-2">
                {{ formatField(getDraftField(field.id)) }}
              </div>
              <span v-if="showComparisonMeta(field.id) && formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
            </div>
            <!-- 有修改過 -->
            <template v-else-if="isDraftChanged(field.id)">
              <!-- 正式資料 -->
              <div class="field content font-body-2 old-data">
                <span class="data-label">上次填寫</span>
                <TextDiff onlyRemoved :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                <span v-if="formatDataMonth(formalSubmittedAt)" class="data-month font-body-2">{{ formatDataMonth(formalSubmittedAt) }}</span>
              </div>
              <!-- 草稿/本次填寫 -->
              <div class="field content font-body-2">
                <span class="data-label">{{ currentDataLabel }}</span>
                <TextDiff onlyAdded :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                <span v-if="formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
              </div>
            </template>
            <!-- 保銀留存資料只作為比對依據 -->
            <div v-for="(reference, index) in referenceRows(field.id)" :key="`${field.id}-${index}`" class="field content font-body-2 reference-data">
              <span v-if="showComparisonMeta(field.id)" class="reference-label">
                {{ referenceLabel(field.id, index) }}
                <small v-if="referenceOwnerName(reference)" class="reference-owner">{{ referenceOwnerName(reference) }}</small>
              </span>
              <span class="reference-value">{{ formatField(reference.value) }}</span>
              <span v-if="showComparisonMeta(field.id) && formatDataMonth(reference.rpt_date || reference.upd_datetime)" class="data-month font-body-2">{{ formatDataMonth(reference.rpt_date || reference.upd_datetime) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <div class="info-list" :class="bankBlockClasses" v-if="bank">
      <div class="list-head">
        <h6 class="bold">銀行帳戶資料</h6>
        <v-btn
          v-if="editable && onEdit" @click="onEdit(holder ?? null, bank)"
          variant="plain" class="btn-edit">
          <v-icon icon="edit" size="18" class="mr-1" />編輯
        </v-btn>
      </div>
      <div class="list-body">
        <div v-for="field in bankFields" class="body-row">
          <!-- Checkbox -->
          <template v-if="field.type === 'checkbox'">
            <div class="check-item">
              <v-icon icon="check_box" color="rgba(0, 0, 0, 0.38)" class="mr-2"></v-icon>
              <span>{{ field.name }}</span>
            </div>
          </template>
          <!-- 一般欄位 -->
          <template v-else>
            <div class="field title">
              <div class="font-body-2 bold">
                {{ field.name }}
              </div>
            </div>
            <!-- 銀行帳戶類型單獨處理 -->
            <div class="field content" v-if="field.id === 'bank_account_type'">
              <div class="font-body-2">
                {{ bankAccountTypeDisplay }}
              </div>
            </div>
            <!-- 修改前後對照 -->
            <div class="group" v-else>
              <!-- 沒有修改過 -->
              <div class="field content" v-if="!isDraftChanged(field.id) && !hideEmptyDraftValue(field.id)">
                <span v-if="showComparisonMeta(field.id)" class="data-label">{{ currentDataLabel }}</span>
                <div class="font-body-2">
                  {{ formatField(getDraftField(field.id)) }}
                </div>
                <span v-if="showComparisonMeta(field.id) && formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
              </div>
              <!-- 有修改過 -->
              <template v-else-if="isDraftChanged(field.id)">
                <!-- 正式資料 -->
                <div class="field content font-body-2 old-data">
                  <span class="data-label">上次填寫</span>
                  <TextDiff onlyRemoved :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                  <span v-if="formatDataMonth(formalSubmittedAt)" class="data-month font-body-2">{{ formatDataMonth(formalSubmittedAt) }}</span>
                </div>
                <!-- 草稿/本次填寫 -->
                <div class="field content font-body-2">
                  <span class="data-label">{{ currentDataLabel }}</span>
                  <TextDiff onlyAdded :textA="formatField(getSourceField(field.id))" :textB="formatField(getDraftField(field.id))"></TextDiff>
                  <span v-if="formatDataMonth(currentMonth)" class="data-month font-body-2">{{ formatDataMonth(currentMonth) }}</span>
                </div>
              </template>
              <!-- 保銀留存資料只作為比對依據 -->
              <div v-for="(reference, index) in referenceRows(field.id)" :key="`${field.id}-${index}`" class="field content font-body-2 reference-data">
                <span v-if="showComparisonMeta(field.id)" class="reference-label">
                  {{ referenceLabel(field.id, index) }}
                  <small v-if="referenceOwnerName(reference)" class="reference-owner">{{ referenceOwnerName(reference) }}</small>
                </span>
                <span class="reference-value">{{ formatField(reference.value) }}</span>
                <span v-if="showComparisonMeta(field.id) && formatDataMonth(reference.rpt_date || reference.upd_datetime)" class="data-month font-body-2">{{ formatDataMonth(reference.rpt_date || reference.upd_datetime) }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import moment from 'moment'
import {
  AccountType, getField, FormField,
  Holder, Shareholder, isHolder, isShareholder,
  getHolderName,
  isInstitutionalAccount,
  IHolderProfile,
  IHolderContact,
  IHolderBankAccount,
  IName, IPhoneNumber, IAddress,
} from '@/api/dealings'
import {
  areReferenceFieldValuesEqual,
  filterRelevantReferenceData,
  getHolderFieldValue,
  isReferenceFieldEqual,
  type ReferenceComparisonContext,
} from '@/lib/accountReferenceData'

const props = withDefaults(defineProps<{
  holder?: Holder | Shareholder | null
  bank?: IHolderBankAccount
  referenceData?: {
    field: string,
    value: string | null
    ord?: number | null
    rpt_date?: string | null
    upd_datetime?: string | null
  }[]
  sourceHolder?: Holder | Shareholder | null
  sourceBank?: IHolderBankAccount | null
  diff?: boolean
  isInitial?: boolean
  currentMonth?: string | null
  formalSubmittedAt?: string | null
  comparisonContext: ReferenceComparisonContext
  accountType: AccountType
  editable?: true
  onEdit?: (holder: Holder | Shareholder | null, target: any) => void
}>(), {
  // NOTE: diff 是 boolean 型別 prop，未傳入時 Vue 會自動轉型為 false 而非 undefined，
  // 必須明確給 default: true，否則所有呼叫端都會被當成「明確關閉 diff」而永遠不顯示異動。
  diff: true,
})

const {
  holder,
  bank,
  sourceHolder,
  sourceBank,
  accountType,
  referenceData,
  diff,
  isInitial,
  comparisonContext,
  currentMonth,
  formalSubmittedAt,
} = toRefs(props)
const { admin: isAdmin } = useViewMode()

const chinaDeclarationLabel = computed(() => (
  isInstitutionalAccount(accountType.value)
    ? '是否有中國籍之最終受益人'
    : '是否涉及中國籍投資人'
))

const profileFields = computed(() => {
  const fields: (keyof IHolderProfile)[] = [
    'business_name',
    'business_location',
    'business_id_number',
    'personal_name',
    'birthplace',
    'date_of_birth',
    'nationality',
    'passport_issuing_place',
    'passport_number',
    'tax_payment_location',
    'tax_id_number',
    'no_tin_reason',
    'no_tin_explain',
    'second_nationality',
    'second_tax_payment_location',
    'second_tax_id_number',
    'second_no_tin_reason',
    'second_no_tin_explain',
    'source_of_funds',
    ...(isHolder(holder.value) ? ['shareholder_relationship'] : []),
  ] as (keyof IHolderProfile)[]
  return fields.map(id => getField(id, accountType.value)).filter((exists): exists is FormField => !!exists)
})

const contactFields = computed(() => {
  // 聯名戶只會有一個郵寄地址，掛在主認購者所填的資料中，shareholder 不需要
  const noMailingAddress = accountType.value === AccountType.Joint && isShareholder(holder.value)
  const fields: (keyof IHolderContact)[] = [
    'phone_number',
    'fax_number',
    'email',
    'residence_address',
    'company_address',
    ...(noMailingAddress ? [] : ['mailing_address']),
  ] as (keyof IHolderContact)[]
  return fields.map(id => getField(id, accountType.value)).filter((exists): exists is FormField => !!exists)
})

const bankFields = computed(() => {
  const showAccountType = tradeType.value !== 'SBS'
  // 是 RDM 且當銀行帳戶選擇「聯名戶」時才顯示
  const showJointMatchData = tradeType.value === 'RDM' && bank.value?.bank_account_type === 'Joint Account'
  return (Array<keyof IHolderBankAccount>(
    'bank_name',
    'bank_swift_code',
    'bank_address',
    'beneficiary_bank_account_number',
    'bank_account_name',
    'bank_account_number',
    'intermediary_bank_name',
    'intermediary_bank_swift_code',
    'second_intermediary_bank_account_name',
    'second_intermediary_bank_account_number',
    'bank_remark',
    ...(showAccountType ? ['bank_account_type'] : []) as (keyof IHolderBankAccount)[],
    ...(showJointMatchData ? ['bank_joint_data_match'] : []) as (keyof IHolderBankAccount)[],
    'bank_account_user_confirmed',
  )).map(id => getField(id, accountType.value)).filter((exists): exists is FormField => !!exists)
})

const tradeType = computed(() => {
  return inject<'SBS' | 'RDM' | 'SWITCH' | null>('tradeType', null)
})

/** 贖回的銀行帳戶資訊必須放第一個 */
const bankBlockClasses = computed(() => {
  if (tradeType.value === 'RDM') return ['order-first', 'mb-8']
  else return ['mt-8']
})

function isName(field: any): field is IName { return !!field?.last_name }
function isPhoneNumber(field: any): field is IPhoneNumber[] {
  if (field instanceof Array) {
    return field.find(f => f.hasOwnProperty('phone_number'))
  } else {
    return false
  }
}
function isAddress(field: any): field is IAddress { return !!field?.street }

function formatField(field: string | boolean | IName | IPhoneNumber[] | IAddress | null | any[]): string {
  // 有值才顯示，否則顯示 N/A
  if (typeof field === 'string' && field) return field
  if (typeof field === 'boolean') return field ? '是' : '否'
  if (isName(field)) return [field.last_name, field.first_name].filter((exists): exists is string => !!exists).join(', ')
  if (isPhoneNumber(field)) {
    return combinePhoneNumber(field)
  }
  if (isAddress(field)) return [field.street, field.city, field.county, field.postal_code, field.country].filter(f => f).join(', ')
  // 多筆資料用逗號隔開
  if (field instanceof Array && field.some(exist => exist)) return field.filter(exist => exist).join(', ')
  return 'N/A'
}

function combinePhoneNumber(phoneNumber: IPhoneNumber[]) {
  const result = phoneNumber.map(f => {
    if (f.area_code || f.country_code || f.phone_number) {
      return f.area_code
        ? `+${f.country_code}-${f.area_code.replace(/\b(0+)/gi,"")}-${f.phone_number}` // phone
        : `+${f.country_code}-${f.phone_number?.replace(/\b(0+)/gi,"")}` // mobile
    } else {
      return ''
    }
  }).filter((exists): exists is string => !!exists)
  return result.length ? result.join(', ') : 'N/A'
}

function getRefDataList(fieldId: string) {
  const rows = comparisonContext.value === 'transaction'
    ? filterRelevantReferenceData(referenceData?.value, !!isInitial.value, formalSubmittedAt.value)
    : referenceData?.value || []
  return rows.filter(f => f.field === fieldId)
}

function showReferenceRow(fieldId: string) {
  const references = getRefDataList(fieldId)
  return references.length && !areReferenceFieldValuesEqual(fieldId, getDraftField(fieldId), references)
}

/**
 * 客戶專區（comparisonContext === 'client'）在網站/正式資料本身為空、且下方會顯示保銀留存資料時，
 * 直接隱藏整列，避免顯示一列空的 N/A。
 * 僅限客戶專區：其他畫面仍保留空值列，供 Admin 覆核。
 */
function hideEmptyDraftValue(fieldId: string) {
  return comparisonContext.value === 'client'
    && showReferenceRow(fieldId)
    && formatField(getDraftField(fieldId)) === 'N/A'
}

function referenceRows(fieldId: string) {
  return showReferenceRow(fieldId) ? getRefDataList(fieldId) : []
}

/** 只有同欄位實際呈現兩列以上資料時，才顯示資料來源標題與月份。 */
function showComparisonMeta(fieldId: string) {
  const currentRows = hideEmptyDraftValue(fieldId) ? 0 : (isDraftChanged(fieldId) ? 2 : 1)
  return currentRows + referenceRows(fieldId).length > 1
}

/** 同欄位只有一筆時不加標號；多筆依畫面順序使用小寫羅馬數字。 */
function referenceLabel(fieldId: string, index: number) {
  const labels = ['i', 'ii', 'iii', 'iv']
  return getRefDataList(fieldId).length > 1 ? `保銀留存 ${labels[index] || index + 1}` : '保銀留存'
}

/** 聯名戶只有明確 ord 的保銀資料才補上所屬認購人姓名。 */
function referenceOwnerName(reference: { ord?: number | null }) {
  return accountType.value === AccountType.Joint && reference.ord !== null && reference.ord !== undefined
    ? getHolderName(holder.value)
    : ''
}

/** 將 API 的 yyyyMMdd、yyyyMM 或日期字串統一顯示為 yyyy/MM。 */
function formatDataMonth(value?: string | null) {
  if (!value) return ''
  const date = moment(String(value).trim(), ['YYYYMMDD', 'YYYYMM', 'YYYY/MM', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601], true)
  return date.isValid() ? date.format('YYYY/MM') : ''
}

function getSourceField(fieldId: string) {
  if (sourceBank.value && fieldId in sourceBank.value) return (sourceBank.value as any)[fieldId]
  if (bank.value && fieldId in bank.value) return (bank.value as any)[fieldId]
  return getHolderFieldValue(sourceHolder.value || holder.value, fieldId)
}

function getDraftField(fieldId: string) {
  if (bank.value && fieldId in bank.value) return (bank.value as any)[fieldId]
  return getHolderFieldValue(holder.value, fieldId)
}

function isDraftChanged(fieldId: string) {
  if (diff.value === false) return false
  if (formatField(getSourceField(fieldId)) === 'N/A') return false
  return !isReferenceFieldEqual(fieldId, getSourceField(fieldId), getDraftField(fieldId))
}

const currentDataLabel = computed(() => comparisonContext.value === 'client' ? '網站資料' : '本次填寫')

const bankAccountTypeDisplay = computed(() => {
  let result
  const type = bank.value?.bank_account_type
  if (type === 'Individual Account') result = '單名帳戶'
  if (type === 'Joint Account') result = '聯名帳戶'
  return result ?? 'N/A'
})

</script>

<style scoped lang="stylus">
.info-list
  border 1px solid #E0E0E0
  border-radius 4px
  overflow hidden
  .list-head
    display flex
    justify-content space-between
    align-items center
    padding 16px 12px
    border-bottom 1px solid #E0E0E0
    &:hover
      background #ECEFF1
  .body-row
    display flex
    min-height 48px
    box-shadow 0px -1px 0px 0px rgba(0, 0, 0, 0.06) inset
    &:nth-child(odd)
      background $gray-lighten-5
    .field
      padding 8px 12px
      display flex
      align-items center
    .title
      flex 0 0 200px
      +under(sm)
        flex 0 0 120px
    .group
      flex 1 1 0
      display flex
      flex-direction column
    .content
      flex 1 1 0
      border-left 1px solid #E0E0E0
      border-bottom 1px solid #E0E0E0
      width 100%
      &:last-child
        border-bottom none
      
  .check-item
    display flex
    align-items center
    padding 8px 12px
.btn-edit
  &:hover
    color $primary

.highlight
  color $error
  font-weight bold

.old-data
  color $black-0-38

.reference-label
  color $gray-darken-1
  flex 0 0 136px
  font-size 14px
  display flex
  flex-direction column

.reference-owner
  color $black-0-38
  font-size 12px
  line-height 18px

.data-label
  color $black-0-58
  flex 0 0 136px
  font-size 14px

.reference-data
  color $gray-darken-1

.reference-value
  flex 1 1 auto

.data-month
  color $gray-darken-1
  flex 0 0 auto
  margin-left auto
  padding-left 16px
  white-space nowrap

</style>
